<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
  >
    <div
      class="bg-white w-full max-w-2xl rounded-2xl shadow-xl p-6 relative overflow-y-auto max-h-[85vh] border border-gray-200"
    >
      <h2
        class="text-xl font-bold text-txt-primary-color mb-5 border-b pb-3 text-center"
      >
        Applicant Details
      </h2>

      <div class="flex items-center gap-4 mb-5 border-b pb-4">
        <img
          :src="
            localApplicant?.profilePhoto ||
            'https://cdn-icons-png.flaticon.com/512/4128/4128253.png'
          "
          alt="Applicant Photo"
          class="w-16 h-16 rounded-full object-cover border-2 border-ds-button-color shadow-sm"
        />
        <div>
          <h3 class="text-lg font-bold text-gray-900">
            {{ localApplicant?.name || 'N/A' }}
          </h3>
          <p class="text-gray-600 text-sm font-medium">
            {{ localApplicant?.designation || 'N/A' }}
          </p>
          <p class="text-xs text-gray-500">
            Broker:
            <span class="font-semibold text-gray-800">
              {{ localApplicant?.brokername || 'N/A' }}
            </span>
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div
          class="bg-gray-50 border-l-4 border-blue-500 rounded-lg p-3 shadow-sm"
        >
          <p class="text-xs text-gray-500 font-medium">Birthdate</p>
          <p class="text-sm font-semibold text-gray-900">
            {{ bdayFormat(localApplicant?.birthdate) || 'N/A' }}
          </p>
        </div>
        <div
          class="bg-gray-50 border-l-4 border-blue-500 rounded-lg p-3 shadow-sm"
        >
          <p class="text-xs text-gray-500 font-medium">Email</p>
          <p class="text-sm font-semibold text-gray-900">
            {{ localApplicant?.email || 'N/A' }}
          </p>
        </div>
        <div
          class="bg-gray-50 border-l-4 border-blue-500 rounded-lg p-3 shadow-sm"
        >
          <p class="text-xs text-gray-500 font-medium">Phone</p>
          <p class="text-sm font-semibold text-gray-900">
            {{ localApplicant?.cellphoneno || 'N/A' }}
          </p>
        </div>
        <div
          class="bg-gray-50 border-l-4 border-blue-500 rounded-lg p-3 shadow-sm"
        >
          <p class="text-xs text-gray-500 font-medium">Address</p>
          <p class="text-sm font-semibold text-gray-900">
            {{ localApplicant?.address || 'N/A' }}
          </p>
        </div>
      </div>

      <div
        class="bg-gray-50 border-l-4 rounded-lg p-4 shadow-sm flex items-center justify-between mb-6"
        :class="isHired ? 'border-green-500' : 'border-rose-500'"
        v-if="userRole !== 'staff'"
      >
        <div>
          <p class="text-xs text-gray-500 font-medium">Application Status</p>
          <p
            class="text-sm font-semibold"
            :class="isHired ? 'text-green-600' : 'text-rose-500'"
          >
            {{ isHired ? 'Hired' : 'Pending' }}
          </p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            v-model="isHired"
            class="sr-only peer"
            @change="confirmHire"
          />
          <div
            class="w-10 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition"
          ></div>
          <span
            class="absolute left-[2px] top-[2px] w-4 h-4 bg-white rounded-full transition-all peer-checked:translate-x-5"
          ></span>
        </label>
      </div>

      <div class="mb-4">
        <button
          @click="showDetails = !showDetails"
          class="flex items-center justify-between w-full bg-white border rounded-lg px-4 py-2 text-sm font-medium shadow-sm hover:bg-gray-50 transition"
        >
          <span>Additional Information</span>
          <svg
            :class="showDetails ? 'rotate-180' : ''"
            class="w-4 h-4 transition-transform"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <transition name="fade">
          <div
            v-if="showDetails"
            class="mt-2 bg-gray-50 border rounded-lg p-4 text-sm space-y-1"
          >
            <p>
              <span class="font-medium">Agent Code:</span>
              {{ agentCode || 'N/A' }}
            </p>
            <p>
              <span class="font-medium">TIN:</span> {{ localApplicant.tin }}
            </p>
            <p>
              <span class="font-medium">SSS:</span> {{ localApplicant.sss }}
            </p>
            <p>
              <span class="font-medium">Emergency Name:</span>
              {{ localApplicant.emerName }}
            </p>
            <p>
              <span class="font-medium">Emergency Address:</span>
              {{ localApplicant.emerAddress }}
            </p>
            <p>
              <span class="font-medium">Emergency Phone:</span>
              {{ localApplicant.emerCellphoneno }}
            </p>
          </div>
        </transition>
      </div>

      <h3 class="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">
        Documents
      </h3>

      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <div
          v-for="file in downloadableFiles"
          :key="file.key"
          class="flex flex-col items-center bg-gray-50 border border-gray-200 rounded-xl p-3 shadow-sm hover:shadow-lg transition-all duration-200"
        >
          <div class="flex justify-center mb-2">
            <img
              :src="file.icon"
              alt="file icon"
              class="w-10 h-10 object-contain opacity-90"
            />
          </div>
          <span class="text-xs font-semibold text-gray-800 mb-2 text-center">
            {{ file.label }}
          </span>
          <button
            @click="
              file.label === 'QR Code'
                ? generateAndDownloadQR()
                : downloadFile(localApplicant[file.key], file.label)
            "
            :disabled="file.label !== 'QR Code' && !localApplicant[file.key]"
            class="w-full text-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
            :class="{
              'bg-[#f29900] hover:bg-[#d98800] text-white shadow-sm hover:shadow-md':
                file.label === 'QR Code' || localApplicant[file.key],
              'bg-gray-300 text-gray-600 cursor-not-allowed':
                file.label !== 'QR Code' && !localApplicant[file.key],
            }"
          >
            Download
          </button>
        </div>
      </div>

      <div class="flex justify-end mt-8">
        <button
          @click="close"
          class="bg-ds-button-color text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, toRef, computed, watch } from 'vue';
import QRCode from 'qrcode';
import { bdayFormat } from '@/utils/bdayFormat.js';

export default {
  props: {
    isVisible: Boolean,
    applicant: Object,
  },
  emits: ['close', 'hired'],
  setup(props, { emit }) {
    const axios = inject('$axios');
    const Swal = inject('$swal');
    const store = inject('$store');
    const close = () => emit('close');
    const localApplicant = toRef(props, 'applicant');
    const isHired = ref(false);
    const userRole = computed(() => store.state.adminLogDetails.role);
    const name = computed(() => store.state.adminLogDetails.name);
    const showDetails = ref(false);
    const agentDetails = ref(null);

    const downloadableFiles = ref([
      {
        label: '1x1 Picture',
        key: 'profilePhoto',
        icon: 'https://cdn-icons-png.flaticon.com/512/847/847969.png',
      },
      {
        label: 'Valid ID',
        key: 'validId',
        icon: 'https://cdn-icons-png.flaticon.com/512/2920/2920244.png',
      },
      {
        label: 'Resume',
        key: 'resume',
        icon: 'https://cdn-icons-png.flaticon.com/512/4725/4725970.png',
      },
      {
        label: 'QR Code',
        key: 'qr',
        icon: require('@/assets/images/qr-code.png'),
      },
    ]);

    watch(
      () => props.applicant?.public_id,
      (newPublicId) => {
        if (newPublicId) {
          fetchAgentAdditional(newPublicId);
        }
      },
      { immediate: true }
    );

    const downloadFile = (url, label) => {
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      link.download = label;
      link.target = '_blank';
      link.click();
    };

    const generateAndDownloadQR = async () => {
      try {
        const publicId = localApplicant.value?.public_id;
        if (!publicId) {
          Swal.fire({
            title: 'Missing ID',
            text: 'This applicant does not have a public ID.',
            icon: 'warning',
          });
          return;
        }

        const qrLink = `https://agent.iwcsrvr.com/#/agent/${publicId}`;
        const qrDataUrl = await QRCode.toDataURL(qrLink, {
          width: 300,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' },
        });

        const link = document.createElement('a');
        link.href = qrDataUrl;
        link.download = `${displayName.value || 'applicant'}_QR.png`;
        link.click();
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: 'Failed to generate QR code. Please try again.',
          icon: 'error',
        });
      }
    };

    const confirmHire = async () => {
      if (!isHired.value) return;

      const result = await Swal.fire({
        title: `Hire ${localApplicant.value?.name || 'this applicant'}?`,
        text: 'Are you sure you want to update this applicant as hired?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#16a34a',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, hire',
        cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) {
        try {
          const applicantId = localApplicant.value?.id;
          const response = await axios.put(
            `/agent/registration/verification/${applicantId}`,
            { isPendingAppv: false }
          );

          if (response.data.status === true) {
            await Swal.fire({
              title: 'Applicant',
              text: response.data.message,
              icon: 'success',
              confirmButtonColor: '#16a34a',
            });
            emit('hired', applicantId);
          } else {
            isHired.value = false;
            Swal.fire({
              title: 'Error',
              text: response.data.message || 'Failed to update status.',
              icon: 'error',
            });
          }
        } catch (err) {
          isHired.value = false;
          Swal.fire({
            title: 'Request Failed',
            text: 'Unable to connect to the server. Please try again later.',
            icon: 'error',
          });
        }
      } else {
        isHired.value = false;
      }
    };
    const displayName = computed(() => {
      if (!localApplicant.value) return 'N/A';

      return userRole.value === 'staff'
        ? localApplicant.value.agentname
        : localApplicant.value.name;
    });

    const agentCode = computed(
      () => agentDetails.value?.data?.[0]?.Agentcode || 'N/A'
    );

    const fetchAgentAdditional = async (publicId) => {
      try {
        const response = await axios.get(
          `https://deped-api.onrender.com/wb/general/agent/${publicId}`
        );
        agentDetails.value = response.data;
      } catch (err) {
        console.error('Error fetching agent info:', err);
      }
    };

    return {
      close,
      localApplicant,
      downloadableFiles,
      downloadFile,
      isHired,
      confirmHire,
      bdayFormat,
      userRole,
      name,
      generateAndDownloadQR,
      displayName,
      showDetails,
      agentDetails,
      agentCode,
    };
  },
};
</script>
